import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Export, Paging, Pager,
  Grouping, GroupPanel, Summary, TotalItem, Toolbar, Item as ToolbarItem
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import notify from 'devextreme/ui/notify';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from "exceljs";
import { saveAs } from 'file-saver';
import API_URL from '../config';

const LOT_BIN_API = `${API_URL}/api/oracle/lot-summary-bin-report`; 
const sbinCountList = [50, 100, 150, 200, 250, 300]; 

const styles = `
.loading-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.7); display: flex; flex-direction: column; justify-content: center; align-items: center; z-index: 10; }
.loading-text { margin-top: 10px; font-size: 16px; color: #333; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.loading-spinner { border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; }
`;

const formatDateTime = (cellInfo) => {
    const val = cellInfo.value;
    if (!val || val.length < 14) return val;
    return `${val.substring(0, 4)}-${val.substring(4, 6)}-${val.substring(6, 8)} ${val.substring(8, 10)}:${val.substring(10, 12)}:${val.substring(12, 14)}`;
};

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
};

export default function LotSummaryBinReport({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [gridData, setGridData] = useState([]); 
  const [loading, setLoading] = useState(false);
  
  const isCustFixed = user && user.role === 'CUST';

  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);

  const [selectedCust, setSelectedCust] = useState(isCustFixed ? user.username : null);
  const [selectedDevice, setSelectedDevice] = useState('ALL'); // 기본값 ALL
  const [selectedOper, setSelectedOper] = useState('ALL');     // 기본값 ALL
  const [lotNo, setLotNo] = useState('');
  
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([{ CODE_CD: 'ALL' }]);
  const [operList, setOperList] = useState([{ CODE_CD: 'ALL' }]);
  
  const [error, setError] = useState("");
  const [binCount, setBinCount] = useState(100);

  // 1. 날짜 변경 -> CUST 목록 조회
  useEffect(() => {
    if (!isCustFixed) setSelectedCust(null);
    setSelectedDevice('ALL'); setSelectedOper('ALL');
    setCustList([]); setDeviceList([{ CODE_CD: 'ALL' }]); setOperList([{ CODE_CD: 'ALL' }]);

    const st = formatDate(dateFrom);
    const en = formatDate(dateTo);
    
    axios.get(`${API_URL}/api/oracle/combo/cust`, { params: { dateFrom: st, dateTo: en } })
      .then(res => {
        setCustList(res.data.data || []);
        if (isCustFixed) setSelectedCust(user.username);
      });
  }, [dateFrom, dateTo, isCustFixed, user]);

  // 2. CUST 변경 -> DEVICE 목록 조회 (ALL 포함)
  useEffect(() => {
    setSelectedDevice('ALL'); setSelectedOper('ALL');
    setDeviceList([{ CODE_CD: 'ALL' }]); setOperList([{ CODE_CD: 'ALL' }]);

    if (!selectedCust) return;

    const st = formatDate(dateFrom);
    const en = formatDate(dateTo);

    axios.get(`${API_URL}/api/oracle/combo/device`, { params: { dateFrom: st, dateTo: en, custCd: selectedCust } })
      .then(res => {
        const data = res.data.data || [];
        setDeviceList([{ CODE_CD: 'ALL' }, ...data]);
      });
  }, [selectedCust, dateFrom, dateTo]);

  // 3. DEVICE 변경 -> OPER 목록 조회 (ALL 포함)
  useEffect(() => {
    setSelectedOper('ALL'); 
    setOperList([{ CODE_CD: 'ALL' }]);

    if (!selectedCust || !selectedDevice) return;

    const st = formatDate(dateFrom);
    const en = formatDate(dateTo);
    
    // DB의 콤보박스 프로시저가 ALL을 처리하지 못할 수 있으므로, ALL일 때는 빈 문자열을 보냄
    const devCdParam = selectedDevice === 'ALL' ? '' : selectedDevice;

    axios.get(`${API_URL}/api/oracle/combo/oper`, { params: { dateFrom: st, dateTo: en, custCd: selectedCust, deviCd: devCdParam } })
      .then(res => {
        const data = res.data.data || [];
        setOperList([{ CODE_CD: 'ALL' }, ...data]);
      });
  }, [selectedDevice, selectedCust, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    // CUST만 필수값으로 체크 (DEVICE, OPER는 ALL이 가능하므로 체크 완화)
    if (!selectedCust) {
      notify("Customer(CUST) 항목을 선택하세요.", "warning", 2000);
      return;
    }
    
    setLoading(true); setError(""); setGridData([]);
    
    try {
      const params = {
        custCd: selectedCust, 
        deviCd: selectedDevice || 'ALL', 
        operCd: selectedOper || 'ALL',
        lotNo: lotNo.trim() || 'ALL', 
        dateFrom: formatDate(dateFrom), 
        dateTo: formatDate(dateTo),
      };
      
      const response = await axios.get(LOT_BIN_API, { params });
      
      const responseData = response.data || { data: [], retMsg: '응답 없음' };
      if (responseData.retCode !== 0) throw new Error(responseData.retMsg);
      
      const data = responseData.data || [];
      if (data.length === 0) notify("조회된 데이터가 없습니다.", "info", 2000);
      setGridData(data);
    } catch (error) {
      setError(`조회 실패: ${error.message}`);
      notify(`조회 실패: ${error.message}`, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [selectedCust, selectedDevice, selectedOper, lotNo, dateFrom, dateTo]);

  const onExporting = useCallback((e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Lot_Summary_Report");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Lot_Summary_Report.xlsx"));
    e.cancel = true;
  }, []);

  const columns = useMemo(() => {
    const fixedColumns = [
      { dataField: "devi_cd", caption: "Device Id", width: 140, fixed: true },
      { dataField: "lot_no", caption: "Lot Id", width: 110, fixed: true },
      { dataField: "oper_cd", caption: "Oper", width: 80, fixed: true },
      { dataField: "tester", caption: "Tester Id", width: 100 },
      { dataField: "program", caption: "Program", width: 150 }, 
      { dataField: "start_time", caption: "Start Time", width: 150, dataType: "string", customizeText: formatDateTime },
      { dataField: "end_time", caption: "End Time", width: 150, dataType: "string", customizeText: formatDateTime },
      { dataField: "yld", caption: "Yld", dataType: "number", alignment: "right", width: 80, format: "#0.00", customizeText: (c) => c.valueText ? c.valueText + "%" : "" },
      { dataField: "wafer_qty", caption: "Wafer Qty", dataType: "number", alignment: "right", width: 90 },
      { dataField: "tested_die", caption: "Test Die", dataType: "number", alignment: "right",  width: 100 },
      { dataField: "good_die", caption: "Good Die", dataType: "number", alignment: "right",  width: 100 },
    ];

    const binColumns = Array.from({ length: binCount }, (_, i) => {
        return <Column key={`bin${i + 1}`} dataField={`bin${i + 1}`} caption={`BIN${i + 1}`} dataType="number" alignment="right" width={90} />;
    });

    return (
      <>
        <Column caption="Basic Info" isBand={true} fixed={true}>
          {fixedColumns.map(col => <Column key={col.dataField} {...col} />)}
        </Column>
        <Column caption="Bin Data" isBand={true}>{binColumns}</Column>
      </>
    );
  }, [binCount]);

  return (
    <div style={{ padding: 20, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <style>{styles}</style>
      <h2 style={{ marginBottom: '20px' }}>SBIN LOT SUMMARY</h2>
      
      <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center', marginBottom: '20px' }}>
        <DateBox value={dateFrom} onValueChanged={e => setDateFrom(e.value)} label="From" width={150} type="date" displayFormat="yyyy-MM-dd" />
        <DateBox value={dateTo} onValueChanged={e => setDateTo(e.value)} label="To" width={150} type="date" displayFormat="yyyy-MM-dd" />
        
        <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedCust(e.value)} searchEnabled label="Customer" width={180} disabled={isCustFixed} showClearButton={!isCustFixed} placeholder="Select CUST" />
        
        {/* Device, Oper의 disabled 조건 완화 (ALL이 있으므로 배열 길이가 0일 때만 비활성화) */}
        <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="Device" width={200} placeholder="Select DEVICE" disabled={!selectedCust || deviceList.length <= 1} />
        <SelectBox dataSource={operList} value={selectedOper} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedOper(e.value)} searchEnabled label="Operation" width={140} placeholder="Select OPER" disabled={!selectedCust || operList.length <= 1} />
        
        <TextBox value={lotNo} onValueChanged={e => setLotNo(e.value)} placeholder="Lot No." label="Lot No" width={150} />
        <SelectBox dataSource={sbinCountList} value={binCount} onValueChanged={e => setBinCount(e.value)} label="BIN View Count" width={140} />
        
        <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
      </div>

      {error && <p style={{ color: "red", fontWeight: "bold" }}>{error}</p>}

      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"><div className="loading-spinner"></div><div className="loading-text">Loading...</div></div>}
        <DataGrid dataSource={gridData} showBorders={true} columnAutoWidth={false} height="100%" onExporting={onExporting} allowColumnResizing={true} hoverStateEnabled={true} wordWrapEnabled={true} scrolling={{ mode: 'standard', columnRenderingMode: 'virtual' }}>
          {columns}
          <Paging defaultPageSize={25} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[20, 50, 100]} showInfo={true} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Export enabled={true} />
          <GroupPanel visible={false} /> 
          <Grouping autoExpandAll={false} />
          <SearchPanel visible={true} width={240} placeholder="Search..." />
          <Toolbar>
            <ToolbarItem location="after" name="exportButton" />
            <ToolbarItem location="after" name="searchPanel" />
          </Toolbar>
          <Summary>
            <TotalItem column="lot_no" summaryType="count" displayFormat="Total: {0}" />
            <TotalItem column="tested_die" summaryType="sum"  displayFormat="{0}" />
            <TotalItem column="good_die" summaryType="sum"  displayFormat="{0}" />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
}